import "@/styles/globals.css";
// Starts the translations in the remembered language (Dutch on a first visit).
import "@/lib/i18n";

import { ErrorBoundary } from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { App } from "@/App";
import { ErrorFallback } from "@/components/error-fallback";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/api";
import { trackInputModality } from "@/lib/input-modality";
import { reloadAfterDeploy } from "@/lib/reload-after-deploy";
import { initSentry } from "@/lib/sentry";

// Before the first render, so a crash during it is reported too. Without
// VITE_SENTRY_DSN this does nothing, and the error boundary below only shows
// the fallback.
initSentry();
// A page left open across a deploy reloads once instead of showing the fallback.
reloadAfterDeploy();
// Focus outlines for the keyboard only: after a click no element keeps one (globals.css).
trackInputModality();

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary fallback={<ErrorFallback />}>
          {/* A short delay, so sweeping the mouse over a row of icons does not flash every label. */}
          <TooltipProvider delayDuration={150}>
            <App />
            {/* The one place toasts appear (`toast` in src/lib/toast.ts), above every page and dialog. */}
            <Toaster />
          </TooltipProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
);
